import styled from '@emotion/styled';
import { Button, ButtonVariant, IconButton } from 'react-magma-dom';
import { COLORS, DANGER, DOTTED_FOCUS, FOCUS, VERBS_FOCUS } from '../../constants';

type ToolButtonI = {
  isChosen: boolean;
};

export const StyledButton = styled.button`
  padding: 12px 16px 11px 16px;
  outline: none;
  height: 42px;
  border: none;
  cursor: pointer;
  color: ${COLORS.black};
  font-family: Mulish;
  font-size: 15px;
  background: ${COLORS.gray95};
  & span {
    padding: 4px 9px 4px 8px;
  }
  &:hover {
    & span {
      background: ${COLORS.verbsButton};
    }
  }
  &:focus {
    & span {
      background: ${COLORS.verbsButton};
    }
  }
  box-sizing: border-box;
  ${VERBS_FOCUS}
`;

export const StyledIconButton = styled.button<{ isActive: boolean; isClickable: boolean }>`
  width: ${({ isActive }) => (isActive ? '77px' : '92.9px')};
  height: 42px;
  padding: 11.5px 18px;
  border: none;
  border-radius: ${({ isActive }) => (isActive ? '8px 0px 0px 8px' : '8px')};
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  background: ${({ isActive }) => (isActive ? COLORS.verbsButtonActive : COLORS.verbsButton)};
  color: ${COLORS.black};
  font-family: Mulish;
  font-size: 15px;
  cursor: pointer;
  box-sizing: border-box;
  ${({ isClickable }) => (isClickable ? '' : `pointer-events: none; background: ${COLORS.verbsButtonActive};`)}
  ${VERBS_FOCUS}
`;

export const StyledIcon = styled.button`
  width: 32px;
  height: 42px;
  display: flex;
  justify-content: center;
  align-items: center;
  border: none;
  border-radius: 0px 8px 8px 0px;
  cursor: pointer;
  &:hover {
    background: #dfdfdf;
  }
  &:focus {
    background: #dfdfdf;
  }
  ${VERBS_FOCUS}
`;

export const StyledCrossButton = styled(IconButton)<{
  top?: number;
  right?: number;
  width?: number;
  height?: number;
}>`
  position: absolute !important ;

  top: ${({ top }) => top || 20}px!important;
  right: ${({ right }) => right || 20}px!important;
  width: ${({ width }) => width || 40}px!important;
  height: ${({ height }) => height || 40}px!important;
  background-color: ${COLORS.white}!important;
  z-index: 11;

  ${FOCUS}
`;

export const StyledFilledCrossButton = styled(IconButton)<{
  bgColor: string;
}>`
  z-index: 101;
  background-color: ${({ bgColor }) => bgColor};

  top: 6px !important;
  right: 6px !important;

  svg {
    color: ${COLORS.white};
  }

  :hover,
  :focus {
    background-color: ${({ bgColor }) => `${bgColor} !important`};
    opacity: 0.8;
  }

  ${DOTTED_FOCUS}
`;

export const StyledCloseModalButton = styled(IconButton)`
  ${FOCUS}
  position: absolute!important;
  background-color: white;
  z-index: 11;
  right: 10px;
  top: 10px;
`;

export const StyledBurgerButton = styled(IconButton)`
  margin-left: 18px;
  @media (min-width: 1025px) {
    display: none !important;
  }
`;

export const StyledCrossSideButton = styled(IconButton)`
  ${FOCUS}
  position: absolute !important;
  top: 6px;
  right: 6px;
  z-index: 35;

  svg {
    width: 36.5px;
    height: 36.5px;
  }

  :not(:disabled)::after {
    width: 20px;
    height: 20px;
  }
`;

export const StyledToolButton = styled(IconButton)<ToolButtonI>`
  background: ${({ isChosen }) => (isChosen ? '#e6e6e6' : '')};
  color: ${({ isChosen }) => (isChosen ? '#3F3F3F' : '')};
`;

export const StyledProformaButton = styled.button<{
  isLFLabel?: boolean;
  bGColor: string;
  withBorder?: boolean;
  inverted?: boolean;
  withoutMargin?: boolean;
}>`
  background: ${({ bGColor }) => bGColor};
  height: fit-content;

  ${({ isLFLabel }) =>
    isLFLabel
      ? `
        padding: 7.5px 18px;
        font-size: 12px;
    `
      : `
        padding: 10px 24px;
        font-size: 16px;
        line-height: normal;
    `};

  margin-right: ${({ withoutMargin }) => (withoutMargin ? 0 : '10px')};
  margin-bottom: ${({ withoutMargin }) => (withoutMargin ? 0 : '10px')};

  border: ${({ withBorder, bGColor }) => (withBorder ? `1px solid ${COLORS.white}` : `1px solid ${bGColor}`)};
  border-radius: 8px;

  color: ${({ inverted }) => (inverted ? COLORS.white : 'black')};

  font-weight: bold;

  :disabled {
    cursor: not-allowed !important;
    opacity: 0.5 !important;
  }

  cursor: pointer;

  :hover {
    opacity: 0.8;
  }

  font-family: 'Work Sans', sans-serif;

  ${DOTTED_FOCUS};
`;

type ModalBtn = {
  bGColor: string;
  borderColor?: string;
  textColor: string;
  margin?: string;
};

export const StyledModalButton = styled(Button)<ModalBtn>`
  height: 42px;
  text-transform: none;
  background: ${({ bGColor }) => bGColor} !important;

  padding: ${({ borderColor }) => (borderColor ? '9px 23.5px' : '10px 24.5px')};
  margin: ${({ margin }) => margin || '4px'};

  border: ${({ borderColor }) => (borderColor ? `1px solid ${borderColor}` : 'none')};
  border-radius: 8px;

  color: ${({ textColor }) => (textColor ? textColor : 'black')} !important;
  font-size: 16px;
  font-weight: bold;

  :disabled {
    cursor: not-allowed !important;
    opacity: 0.5 !important;
  }

  cursor: pointer;

  :hover {
    opacity: 0.8;
  }

  ${DOTTED_FOCUS};
`;

export const StyledModalLinkButton = styled(Button)<{
  textColor?: string;
}>`
  border: none;
  text-transform: none;
  text-decoration: underline;
  color: ${({ textColor }) => (textColor ? textColor : 'black')} !important;
  font-size: 11px;
  font-weight: bold;
  line-height: 2.36;
  cursor: pointer;
  margin: 0 4px;
  padding: 0;
  height: auto;
  min-width: auto;

  &,
  :hover,
  :focus,
  :active {
    background: none !important;
  }
`;

export const StyledModalConfirmBtn = styled(Button)<ModalBtn>`
  text-transform: none;
  background: ${({ bGColor }) => bGColor} !important;

  padding: ${({ borderColor }) => (borderColor ? '9px 23.5px' : '10px 24px')};

  border: ${({ borderColor }) => (borderColor ? `1px solid ${borderColor}` : 'none')};
  border-radius: 8px;

  color: ${({ textColor }) => (textColor ? textColor : 'black')} !important;
  font-size: 16px;
  font-weight: bold;
  line-height: 22px;

  height: 42px;

  :disabled {
    cursor: not-allowed !important;
    opacity: 0.5 !important;
  }

  cursor: pointer;

  :hover {
    opacity: 0.8;
  }

  ${DOTTED_FOCUS};
`;

export const StyledAddPhotoButton = styled.button<{
  color: string;
}>`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;

  font-family: Lexend;
  font-size: 16px;
  font-weight: 600;
  color: ${({ color }) => color};

  background-color: rgba(255, 255, 255, 0.9);

  border: none;
  border-radius: 5px;

  padding: 8px 8px 6px;

  height: 80px;

  ${DOTTED_FOCUS};

  cursor: pointer;
`;

export const StyledEditPhotoButton = styled.button<{
  textColor: string;
}>`
  position: absolute !important;

  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;

  font-family: Lexend;
  font-size: 12px;
  font-weight: 600;
  color: ${({ textColor }) => textColor};

  background-color: rgba(255, 255, 255, 0.9);

  border: none;
  border-radius: 5px;
  padding: 8px 8px 6px 8px;
  height: 54px;

  ${DOTTED_FOCUS};

  cursor: pointer;
`;

const getControlButtonColor = (isUnderlineTextHidden: boolean): string => {
  return isUnderlineTextHidden ? COLORS.gray95 : 'transparent';
};

export const StyledHybridUnderlineControlButton = styled(Button)<{ isUnderlineTextHidden: boolean }>`
  height: 48px !important;
  background-color: ${({ isUnderlineTextHidden }) => getControlButtonColor(isUnderlineTextHidden)}!important;
  color: black !important;
  border: solid 1px ${COLORS.veryLightPink}!important;
  text-transform: capitalize !important;
  border-radius: 0px !important;
  & > span {
    color: black;
    font-size: 14px !important;
    font-family: Lexend !important;
    font-weight: 600 !important;
    border-bottom: solid 2px ${COLORS.black50}!important;
    ${({ isUnderlineTextHidden }) => (isUnderlineTextHidden ? null : 'opacity: 0.5;')}!important;
  }

  :focus {
    color: ${COLORS.black} !important;
    background: ${({ isUnderlineTextHidden }) => getControlButtonColor(isUnderlineTextHidden)} !important;
  }

  :hover {
    color: ${COLORS.black} !important;
    background: ${({ isUnderlineTextHidden }) => getControlButtonColor(isUnderlineTextHidden)} !important;
  }
`;

export const StyledNavButton = styled(IconButton)`
  padding: ${({ variant }) => (variant === ButtonVariant.solid ? '0 3px 0 7px' : '0px')}!important;
  margin: 5px !important;
  font-size: 15px !important;
`;

export const StyledImageBlockRow = styled.div`
  display: flex !important;
  flex-direction: row !important;
`;

export const StyledImageBlock = styled.div`
  position: relative;
  & svg {
    position: absolute !important;
    left: 112px;
    bottom: 116px;
    z-index: 1;
  }
  &:not(:last-child) {
    margin-right: 10px;
  }
`;

export const StyledImageInput = styled.input<{ selected: boolean; color: string }>`
  width: 129px;
  height: 129px;
  border: ${({ selected, color }) => (selected ? `5px solid ${color}` : `1px solid ${COLORS.veryLightPink}`)};
`;

export const IconButtonStyled = styled(IconButton)<{
  bgColor: string;
}>`
  width: 22px;
  height: 22px;
  margin: 1px 4px;
  &:hover {
    background: ${({ bgColor }) => bgColor} !important;
  }
  &:focus {
    background: ${({ bgColor }) => bgColor} !important;
  }
`;

export const StyledPlayVideoButton = styled(IconButton)`
  z-index: 1;
  position: absolute !important;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  height: 42px;
  width: 42px;
`;

export const StyledFilterButton = styled(Button)`
  text-transform: capitalize;
  padding: 10px 10px;
  line-height: 1.5;
  font-weight: 400;
  height: 26px;
`;

export const StyledShortRelatedDownload = styled.div`
  margin: 0px 0px 0px 10px;
  height: 36px;
  width: 36px;
  border-radius: 45px;
  padding: 5px;
`;

export const StyledDownloadButton = styled(IconButton)``;

export const StyledResetButton = styled.button<{
  color: string;
}>`
  width: 93px;
  height: 42px;
  & > span {
    width: 45px;
    height: 22px;
    display: flex;
    justify-content: center;
    align-items: center;
  }
  display: block;
  outline: none;
  padding: 10px 24px;
  border: 0;
  border-radius: 8px;
  background: ${({ color }) => color};
  margin: 0 auto;
  font-size: 16px;
  font-weight: bold;
  color: ${COLORS.white};
  cursor: pointer;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  flex-grow: 0;
  &:hover,
  &:active,
  &:focus {
    filter: brightness(90%);
  }
  ${DOTTED_FOCUS}
`;

export const StyledDeleteModalButton = styled(StyledResetButton)`
  width: 137px;
  height: 42px;
  & > span {
    width: 89px;
    height: 22px;
  }
`;

export const StyledDeleteButton = styled.button`
  width: 69px;
  height: 20px;
  outline: none;
  border: 0;
  background: none;
  font-size: 12px;
  font-weight: 600;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  ${DANGER}
  ${DOTTED_FOCUS}
`;

export const StyledPrintPageButton = styled.button`
  width: 36px;
  height: 36px;
  border: 0;
  border-radius: 50%;
  background: none;
  margin-bottom: 4px;
  padding: 0;
  cursor: pointer;
  ${DOTTED_FOCUS}

  :disabled {
    cursor: not-allowed;
  }
`;

export const StyledVideoModalCloseButton = styled(IconButton)`
  position: absolute !important;
  top: 7px;
  right: 7px;
`;

export const StyledCloseImagesModalIconButton = styled(IconButton)`
  position: absolute !important;
  top: 7px;
  right: 7px;
  margin: 0;
  padding: 0;
`;

export const StyledTermsOfUseButton = styled.button`
  font-size: 11.5px !important;
  color: ${COLORS.blackOlive}!important;

  font-weight: normal !important;

  font-stretch: normal !important;

  font-style: normal !important;

  -webkit-letter-spacing: normal;
  -moz-letter-spacing: normal;
  -ms-letter-spacing: normal;
  letter-spacing: normal !important;

  padding: 0 5px;
  padding: 0;
  border: none;
  text-decoration: underline;
  cursor: pointer;
  background: none;

  ${DOTTED_FOCUS};
`;

export const StyledTermsOfUseModalDoneButton = styled(Button)`
  min-width: 0px;
  width: 90px;
  height: 42px;
  margin: 20px 0px 0px 0px;
  padding: 12px 14px;
  border-radius: 8px;

  text-transform: none;

  ${DOTTED_FOCUS};
`;

export const StyledSquareImage = styled.img<{ selected: boolean; color: string }>`
  width: 129px;
  height: 129px;
  cursor: pointer;
  object-fit: contain;
  border: ${({ selected, color }) => (selected ? `5px solid ${color}` : `1px solid ${COLORS.veryLightPink}`)};
`;

export const StyledNoMarginButton = styled(Button)`
  margin: 0px;
`;
